import defaultImage from '@/assets/images/placeholders/show-course.jpg';
import { useEffect, useRef, useState } from 'react';
import { CourseCreateStepImageModalPreview } from './course-create-step-image-modal-preview';
import { t } from 'i18next';
import { ReactComponent as Stars } from './img/stars.svg';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import ActionCableConsumer from '@/utils/actionCableConsumer';
import { Button, ButtonIcon } from '@/components/ui/button';
import { PageContent, PageContentHeader, PageContentTitle } from '@/components/custom/page';
import { Label } from '@/components/ui/label';
import { InputFile } from '@/components/ui/input';
import { CourseBanner } from '@/components/custom/course';

export const CourseCreateStepImage = props => {
  const { step, handleChangeImage, file, form, courseId } = props;
  const [webhookNotification, setWebhookNotification] = useState(null);
  const [channel, setChannel] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const loadingToastRef = useRef(null);
  const [fileUrl, setFileUrl] = useState(null);

  const client = useClient();

  const [showModal, setShowModal] = useState(false);

  const handleCreateByAI = () => {
    const title = form?.values?.title;
    const getImage = client.mutate(`generate_image?format=base64&title=${title}`);

    if (!loadingToastRef.current) {
      loadingToastRef.current = toast.loading('Nossa IA está gerando a melhor imagem');
    }

    getImage
      .then(response => {
        setChannel(response.data.channel);
        setRequestId(response.data.request_id);
      })
      .catch(error => {
        toast.error('Erro ao gerar imagem', { id: loadingToastRef.current });
        loadingToastRef.current = null;
      });
  };

  const handleReceivedNotification = data => {
    setWebhookNotification(data);
  };

  useEffect(() => {
    if (!webhookNotification) return;

    if (!loadingToastRef.current) return;

    if (webhookNotification.event === 'failed') {
      toast.error('Erro ao gerar a imagem. Tente novamente mais tarde.', { id: loadingToastRef.current });
    } else if (webhookNotification.event === 'completed') {
      setFileUrl(webhookNotification.file_url);
    }
    setWebhookNotification(null);
    setChannel(null);
    setRequestId(null);
  }, [webhookNotification]);

  async function fetchImageAsBase64(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Falha ao baixar imagem');

    const blob = await res.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  useEffect(() => {
    return () => {
      if (loadingToastRef.current) {
        toast.dismiss(loadingToastRef.current);
        loadingToastRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (step !== 'imagem') {
      setFileUrl(null);
      toast.dismiss();
      loadingToastRef.current = null;
    }
  }, [step]);

  useEffect(() => {
    if (fileUrl) {
      fetchImageAsBase64(fileUrl).then(base64Image => {
        form.setFieldValue('image', base64Image);
        toast.success('Imagem gerada com sucesso!', { id: loadingToastRef.current });
        loadingToastRef.current = null;
      });
    }
  }, [fileUrl]);

  return (
    step === 'imagem' && (
      <div className="mb-12">
        {channel && requestId && (
          <ActionCableConsumer
            channel={channel}
            setObjectChannel={handleReceivedNotification}
            channelKey={'request_id'}
            channelId={requestId}
          />
        )}

        <PageContent
          bg
          className="mb-4 animate-fade-in"
        >
          <PageContentHeader>
            <PageContentTitle>{t('courses.buttonNewCourse')}</PageContentTitle>
          </PageContentHeader>

          <div className="space-y-5">
            <fieldset>
              <Label
                asChild
                className="mb-1"
              >
                <legend>{t('courses.coverImage')} (1020x252 pixels)</legend>
              </Label>

              <div className="form__ai-banner mb-12">
                <h3 className="form__ai-banner-title">Nossa IA te ajuda com a imagem</h3>

                <Button
                  variant="primary-light"
                  size="sm"
                  border
                  type="button"
                  onClick={() => handleCreateByAI()}
                >
                  <ButtonIcon>
                    <Stars className="challenge-success__stars challenge-success__stars--icon" />
                  </ButtonIcon>
                  Criar com ajuda da T.I.A
                </Button>
              </div>

              <InputFile
                label={t('textsCommon.placeholderUploadImage')}
                name="image"
                id="image"
                accept=".jpeg, .png, .jpg"
                onChange={handleChangeImage}
              />
            </fieldset>

            <CourseBanner
              backgroundUrl={form?.values?.image || file}
              className="min-h-[160px]"
            />

            <CourseCreateStepImageModalPreview
              courseId={courseId}
              form={form}
              show={showModal}
              onClose={() => setShowModal(false)}
              src={form?.values?.image || file || defaultImage}
            />
          </div>
        </PageContent>

        <Button
          variant="link"
          wide
          type="button"
          onClick={() => setShowModal(true)}
        >
          {t('courses.previewCourse')}
        </Button>
      </div>
    )
  );
};
